/**
 * State for the admin Notification Setup screen.
 *
 * Loads the live event catalogue and the tenant's master switches, tracks the
 * grid's dirty state, and writes the staged edits back as tenant overrides.
 *
 * Two different save models, deliberately:
 *
 *  - **Master switches and the tenant default frequency** apply immediately
 *    (the email kill switch is something you reach for during an incident — it
 *    must not sit behind a grid save). The confirmation lives in the UI.
 *  - **Per-event rows** stage into `pending` / `resets` and go out on an
 *    explicit "Save changes", so an admin can work through 100+ rows and still
 *    back out.
 *
 * A row whose staged config matches the shipped defaults is saved as a *reset*
 * — every field goes out as its inherit marker (`null`) and the API drops the
 * override row, which is the same thing the per-row revert does. There is no
 * half-configured state to clean up either way.
 *
 * Frequency inherits rather than copies: a row with `frequency: null` tracks
 * the tenant default, so moving the default moves every inherited row with it.
 * Only a row an admin explicitly pinned holds a value of its own.
 *
 * Every write echoes the company the catalogue said it resolved to. The screen
 * displaying one company while the API writes to another is then not merely
 * unlikely but impossible: the server compares the claim with its own scope and
 * answers `409 scope_mismatch` rather than writing.
 *
 * @link ./catalogue for the payload parsing.
 * @link ./saveFailures for how a refusal is classified and worded.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  notificationsAPI,
  type NotificationChannel,
  type NotificationOverrideInput,
  type NotificationSettings,
  type NotificationSettingsUpdate,
  type NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { getErrorMessage } from '../../../../hooks/useFetchData';
import { toast } from 'sonner';
import { parseCatalogue, parseMasterSwitches, readCanConfigure } from './catalogue';
import {
  describeFailures,
  firstScopeFailure,
  readScopeFailure,
  type FailedSave,
} from './saveFailures';
import { FREQUENCY_LABELS } from './types';
import type {
  EventConfig,
  FrequencySummary,
  MasterSwitches,
  SetupCatalogue,
  SetupEvent,
} from './types';

/** Channel lists are stored in a fixed order, so a join comparison is exact. */
const sameChannels = (a: NotificationChannel[], b: NotificationChannel[]): boolean =>
  a.join('|') === b.join('|');

const sameConfig = (a: EventConfig, b: EventConfig): boolean =>
  a.enabled === b.enabled
  && a.frequency === b.frequency
  && sameChannels(a.channels, b.channels);

/**
 * Keep only the staged rows named.
 *
 * Used after a partial save: the rows the server accepted are dropped (the
 * reload brings back server truth for them) while every rejected row stays
 * exactly as the admin left it.
 */
const retainKeys = <T>(current: Record<string, T>, keys: string[]): Record<string, T> => {
  if (!keys.length) return {};
  const kept: Record<string, T> = {};
  for (const key of keys) {
    const value = current[key];
    if (value !== undefined) kept[key] = value;
  }
  return kept;
};

export interface UseNotificationSetupResult {
  loading: boolean;
  error: string | null;
  catalogue: SetupCatalogue | null;
  settings: NotificationSettings | null;
  master: MasterSwitches;
  /** Server truth for "may this admin write?" — false renders the grid read-only. */
  canConfigure: boolean;
  saving: boolean;
  savingSettings: boolean;
  /** Number of rows with staged, unsaved edits. */
  dirtyCount: number;
  /** How many events run on a custom cadence rather than the tenant default. */
  frequencySummary: FrequencySummary;
  /** The config to render for one event — staged edit, reset, or server state. */
  configFor: (event: SetupEvent) => EventConfig;
  isDirty: (event: SetupEvent) => boolean;
  reload: () => Promise<void>;
  updateEvent: (event: SetupEvent, patch: Partial<EventConfig>) => void;
  toggleChannel: (event: SetupEvent, channel: NotificationChannel, on: boolean) => void;
  /**
   * Stage one enabled state across an explicit list of rows.
   *
   * Takes the rows themselves, never a domain key: the grid's bulk buttons sit
   * next to a header the search box has already narrowed, so the caller is the
   * only thing that knows what the admin can actually see. Required rows are
   * skipped on disable.
   */
  setEventsEnabled: (events: SetupEvent[], enabled: boolean) => void;
  resetEvent: (event: SetupEvent) => void;
  discardChanges: () => void;
  saveChanges: () => Promise<void>;
  saveSettings: (patch: NotificationSettingsUpdate, message: string) => Promise<void>;
  setEmailsEnabled: (enabled: boolean) => Promise<void>;
  /** Set the tenant-wide default cadence. Applies immediately, like the switches. */
  setDefaultFrequency: (frequency: NotificationTenantDefaultFrequency) => Promise<void>;
}

/**
 * @returns catalogue, master switches and every mutation the setup grid needs.
 */
export function useNotificationSetup(): UseNotificationSetupResult {
  const [catalogue, setCatalogue] = useState<SetupCatalogue | null>(null);
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [canConfigure, setCanConfigure] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pending, setPending] = useState<Record<string, EventConfig>>({});
  const [resets, setResets] = useState<Record<string, boolean>>({});

  /**
   * Re-read the catalogue and settings.
   *
   * @param keepStaged - event keys whose staged edits survive the reload. Empty
   *   (the default) clears the grid, which is only correct when every staged row
   *   actually landed; a rejected row keeps its edit so the admin can retry it.
   */
  const load = useCallback(async (keepStaged: string[] = []) => {
    setLoading(true);
    setError(null);
    try {
      const [catalogueRes, settingsRes] = await Promise.all([
        notificationsAPI.getEventTypes({ include_inactive: true }),
        notificationsAPI.getSettings(),
      ]);
      const parsed = parseCatalogue(catalogueRes.data);
      setCatalogue(parsed);
      setSettings(settingsRes.data);
      setCanConfigure(readCanConfigure(settingsRes.data, parsed.canConfigure));
      setPending((current) => retainKeys(current, keepStaged));
      setResets((current) => retainKeys(current, keepStaged));
    } catch (err: unknown) {
      // A scope refusal is not a generic failure: the error card has to name
      // the company that was refused, not say "Failed to load".
      const scope = readScopeFailure(err);
      setError(
        scope?.message ?? getErrorMessage(err, 'Failed to load the notification catalogue'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const master = useMemo(() => parseMasterSwitches(settings), [settings]);

  /**
   * The company every write claims to be configuring — the one the header is
   * naming, straight off the catalogue's scope block. `{}` when the server sent
   * no scope block, which is the older-client behaviour the API still accepts.
   */
  const scopeClaim = useMemo<{ company?: number }>(
    () => (catalogue?.companyId != null ? { company: catalogue.companyId } : {}),
    [catalogue],
  );

  const allEvents = useMemo(
    () => (catalogue ? catalogue.domains.flatMap((domain) => domain.events) : []),
    [catalogue],
  );

  const configFor = useCallback(
    (event: SetupEvent): EventConfig => {
      if (resets[event.key]) return event.defaults;
      return pending[event.key] ?? event.effective;
    },
    [pending, resets],
  );

  const isDirty = useCallback(
    (event: SetupEvent): boolean => {
      if (resets[event.key]) {
        // A revert is only a change when there is something to revert.
        return event.isOverridden || !sameConfig(event.effective, event.defaults);
      }
      const staged = pending[event.key];
      return staged !== undefined && !sameConfig(staged, event.effective);
    },
    [pending, resets],
  );

  const dirtyEvents = useMemo(() => allEvents.filter(isDirty), [allEvents, isDirty]);

  // Counts what the admin can *see* right now, staged edits included — the
  // number next to the tenant default has to agree with the "Custom" badges in
  // the grid, not with what the server last stored.
  const frequencySummary = useMemo<FrequencySummary>(
    () => ({
      customised: allEvents.filter((event) => configFor(event).frequency !== null).length,
      total: allEvents.length,
    }),
    [allEvents, configFor],
  );

  const stage = useCallback((event: SetupEvent, next: EventConfig) => {
    setPending((current) => ({ ...current, [event.key]: next }));
    setResets((current) => {
      if (!current[event.key]) return current;
      const copy = { ...current };
      delete copy[event.key];
      return copy;
    });
  }, []);

  const updateEvent = useCallback(
    (event: SetupEvent, patch: Partial<EventConfig>) => {
      stage(event, { ...configFor(event), ...patch });
    },
    [configFor, stage],
  );

  const toggleChannel = useCallback(
    (event: SetupEvent, channel: NotificationChannel, on: boolean) => {
      const current = configFor(event);
      // Order-stable so `sameChannels` stays a string comparison, and never
      // wider than the shipped channels — the API rejects a widening write.
      const next = event.availableChannels.filter((candidate) =>
        candidate === channel ? on : current.channels.includes(candidate),
      );
      stage(event, { ...current, channels: next });
    },
    [configFor, stage],
  );

  const setEventsEnabled = useCallback(
    (events: SetupEvent[], enabled: boolean) => {
      const staged: Record<string, EventConfig> = {};
      for (const event of events) {
        // Required events keep firing whatever the bulk action says — the API
        // refuses to disable them, so the grid must not stage it either.
        if (!enabled && !event.canDisable) continue;
        staged[event.key] = { ...configFor(event), enabled };
      }
      if (!Object.keys(staged).length) return;
      setPending((current) => ({ ...current, ...staged }));
      setResets((current) => {
        const copy = { ...current };
        for (const key of Object.keys(staged)) delete copy[key];
        return copy;
      });
    },
    [configFor],
  );

  const resetEvent = useCallback((event: SetupEvent) => {
    setResets((current) => ({ ...current, [event.key]: true }));
    setPending((current) => {
      if (current[event.key] === undefined) return current;
      const copy = { ...current };
      delete copy[event.key];
      return copy;
    });
  }, []);

  const discardChanges = useCallback(() => {
    setPending({});
    setResets({});
  }, []);

  /**
   * One row's write payload. Anything matching the shipped default goes out as
   * its inherit marker, so an override row only ever holds real differences —
   * and a row where everything matches is deleted by the API.
   *
   * Every payload carries the scope claim, so a row can only ever be written to
   * the company the header is naming.
   */
  const buildInput = useCallback(
    (event: SetupEvent): NotificationOverrideInput => {
      if (resets[event.key]) {
        return {
          ...scopeClaim,
          template_key: event.key,
          is_enabled: null,
          channel_override: null,
          frequency: null,
          dedup_window_override: null,
          audience_rule_override: null,
        };
      }
      const next = configFor(event);
      // Clamp to the shipped channels at the payload boundary. The API refuses
      // any channel the platform does not ship this event on
      // (`channel_widening`), so a widened list can never leave the screen —
      // whatever a stale catalogue or a bulk action staged.
      const channels = event.availableChannels.filter((channel) =>
        next.channels.includes(channel),
      );
      return {
        ...scopeClaim,
        template_key: event.key,
        is_enabled: next.enabled === event.defaults.enabled ? null : next.enabled,
        channel_override: sameChannels(channels, event.defaults.channels)
          ? null
          : channels,
        // Already null when the row inherits, which is the API's own inherit
        // marker — an inheriting row never pins a cadence it would then be
        // stuck on the next time the tenant default moves.
        frequency: next.frequency,
      };
    },
    [configFor, resets, scopeClaim],
  );

  /**
   * Write every staged row.
   *
   * Success is only ever reported when the server accepted the whole batch. A
   * rejected row keeps its staged edit and stays dirty — the grid must never
   * look saved when the server said no, and the admin needs the row back to fix
   * it — while the rows that did land are re-read from the server.
   */
  const saveChanges = useCallback(async () => {
    if (!dirtyEvents.length) return;
    setSaving(true);
    try {
      // Per-row upsert (POST /template-overrides/ is an upsert keyed on
      // template_key). See the tab's docblock for why this is not the single
      // bulk round trip yet.
      const results = await Promise.allSettled(
        dirtyEvents.map((event) => notificationsAPI.createOverride(buildInput(event))),
      );
      const failures: FailedSave[] = [];
      results.forEach((result, index) => {
        const event = dirtyEvents[index];
        if (result.status === 'rejected' && event) {
          failures.push({ event, reason: result.reason });
        }
      });

      if (!failures.length) {
        toast.success(
          `Saved ${dirtyEvents.length} notification${dirtyEvents.length === 1 ? '' : 's'}`,
        );
        await load();
        return;
      }

      // A scope refusal is about the whole request, not one row. A mismatch
      // also drops the staged edits: they were staged against a company this
      // screen is no longer scoped to, and re-saving them is precisely the
      // wrong-company write the claim exists to prevent.
      const scope = firstScopeFailure(failures);
      if (scope) {
        toast.error(scope.message);
        await load(scope.kind === 'mismatch' ? [] : failures.map((f) => f.event.key));
        return;
      }

      toast.error(describeFailures(failures, dirtyEvents.length));
      // Re-read so the accepted rows show server truth, while every rejected
      // row stays staged, dirty and ready to retry. Nothing reports success.
      await load(failures.map((failure) => failure.event.key));
    } catch (err: unknown) {
      const scope = readScopeFailure(err);
      toast.error(scope?.message ?? getErrorMessage(err, 'Failed to save notification settings'));
      if (scope?.kind === 'mismatch') await load();
    } finally {
      setSaving(false);
    }
  }, [buildInput, dirtyEvents, load]);

  const saveSettings = useCallback(
    async (patch: NotificationSettingsUpdate, message: string) => {
      setSavingSettings(true);
      try {
        const response = await notificationsAPI.updateSettings({ ...scopeClaim, ...patch });
        setSettings(response.data);
        setCanConfigure(readCanConfigure(response.data, true));
        toast.success(message);
      } catch (err: unknown) {
        const scope = readScopeFailure(err);
        toast.error(scope?.message ?? getErrorMessage(err, 'Failed to update notification settings'));
        // The view moved: re-read everything, not just the switch row, so the
        // whole screen agrees on which company it is now configuring.
        if (scope?.kind === 'mismatch') {
          await load();
          return;
        }
        // Re-read so a rejected switch snaps back to what the server holds.
        try {
          const fresh = await notificationsAPI.getSettings();
          setSettings(fresh.data);
        } catch {
          /* the toast above already told the admin; leave the row as it was */
        }
      } finally {
        setSavingSettings(false);
      }
    },
    [load, scopeClaim],
  );

  const setEmailsEnabled = useCallback(
    async (enabled: boolean) => {
      setSavingSettings(true);
      try {
        // The kill switch silences a whole tenant, so it claims a company too.
        const response = await notificationsAPI.setEmailsEnabled(enabled, scopeClaim.company);
        setSettings(response.data);
        toast.success(
          enabled
            ? 'Notification emails are back on for this tenant'
            : 'All outbound notification email is now stopped for this tenant',
        );
      } catch (err: unknown) {
        const scope = readScopeFailure(err);
        toast.error(scope?.message ?? getErrorMessage(err, 'Failed to change the email master switch'));
        if (scope?.kind === 'mismatch') {
          await load();
          return;
        }
        try {
          const fresh = await notificationsAPI.getSettings();
          setSettings(fresh.data);
        } catch {
          /* handled by the toast above */
        }
      } finally {
        setSavingSettings(false);
      }
    },
    [load, scopeClaim],
  );

  const setDefaultFrequency = useCallback(
    async (frequency: NotificationTenantDefaultFrequency) => {
      await saveSettings(
        { default_frequency: frequency },
        frequency === ''
          ? 'Every event without its own setting is back to its platform default'
          : 'Every event without its own setting now notifies '
            + FREQUENCY_LABELS[frequency].toLowerCase(),
      );
    },
    [saveSettings],
  );

  return {
    loading,
    error,
    catalogue,
    settings,
    master,
    canConfigure,
    saving,
    savingSettings,
    dirtyCount: dirtyEvents.length,
    frequencySummary,
    configFor,
    isDirty,
    reload: load,
    updateEvent,
    toggleChannel,
    setEventsEnabled,
    resetEvent,
    discardChanges,
    saveChanges,
    saveSettings,
    setEmailsEnabled,
    setDefaultFrequency,
  };
}
